import { t } from "@/i18n-js/instance";
import type { PaywallForm } from "@circle-react/components/Paywalls/Admin/PaywallsAdmin/types/PaywallForm";
import type { CurrentCommunity } from "@circle-react/types/CurrentCommunity";
import type {
  BadgeProperty,
  BenefitProperty,
  PaywallCustomization,
  TestimonialProperty,
} from "@circle-react/types/PaywallCustomization";
import {
  ACTION_TYPE,
  COUNTDOWN_TYPE,
} from "@circle-react/types/PaywallCustomization";
import {
  PRICE_CALLOUT_DEFAULT_COLOR,
  REMAINING_TIME_DEFAULT_VALUE,
  defaultBadge,
  defaultBenefit,
  defaultTestimonial,
  findPriceCalloutOrInitialize,
  findReducedPriceOrInitialize,
} from "./paywallCustomizationHelpers";

const getDefaultArray = <T>(
  input: T[] | null | undefined,
  defaultValue: T,
): T[] => {
  if (input === null || input === undefined || input.length === 0) {
    return [{ ...defaultValue }];
  }

  return input;
};

interface PriceCalloutDefaultHelperProps {
  brandColor?: CurrentCommunity["prefs"]["brand_color"];
  paywallParam?: PaywallForm;
  priceCalloutProperties?: PaywallCustomization["price_callout_properties"];
}

interface ReducedPriceDefaultHelperProps {
  paywallParam?: PaywallForm;
  reducedPriceProperties?: PaywallCustomization["reduced_price_properties"];
}

export const paywallCustomizationDefaultHelpers = {
  reducedPriceProperties: ({
    reducedPriceProperties,
    paywallParam,
  }: ReducedPriceDefaultHelperProps) => ({
    prices: findReducedPriceOrInitialize(
      paywallParam?.stripe_prices_attributes || [],
      reducedPriceProperties?.prices || [],
      paywallParam?.currency,
    ),
  }),
  benefitProperties: (
    benefitProperties?: PaywallCustomization["benefit_properties"],
  ) => ({
    enabled: benefitProperties?.enabled ?? false,
    title:
      benefitProperties?.title ??
      t(
        "settings.paywalls.modal.tabs.checkout.builder.benefits.title_placeholder",
      ),
    benefits: getDefaultArray<BenefitProperty>(
      benefitProperties?.benefits,
      defaultBenefit,
    ),
  }),
  testimonialProperties: (
    testimonialProperties?: PaywallCustomization["testimonial_properties"],
  ) => ({
    enabled: testimonialProperties?.enabled ?? false,
    testimonials: getDefaultArray<TestimonialProperty>(
      testimonialProperties?.testimonials,
      defaultTestimonial,
    ),
  }),
  badgeProperties: (
    badgeProperties?: PaywallCustomization["badge_properties"],
  ) => ({
    enabled: badgeProperties?.enabled ?? false,
    badges: getDefaultArray<BadgeProperty>(
      badgeProperties?.badges,
      defaultBadge,
    ),
  }),
  countdownProperties: (
    countdownProperties?: PaywallCustomization["countdown_properties"],
  ): PaywallCustomization["countdown_properties"] => ({
    enabled: countdownProperties?.enabled ?? false,
    countdown_type:
      countdownProperties?.countdown_type || COUNTDOWN_TYPE.REMAINING_TIME,
    remaining_time:
      countdownProperties?.remaining_time || REMAINING_TIME_DEFAULT_VALUE,
    remaining_time_interval: "minutes",
    finish_at: countdownProperties?.finish_at
      ? new Date(countdownProperties?.finish_at).toString()
      : new Date().toString(),
    displayed_text:
      countdownProperties?.displayed_text ||
      t(
        "settings.paywalls.modal.tabs.checkout.builder.countdown.fields.displayed_text_default",
      ),
    action: {
      action_type:
        countdownProperties?.action?.action_type || ACTION_TYPE.DO_NOTHING,
      redirect_to: countdownProperties?.action?.redirect_to || "",
    },
  }),
  priceCalloutProperties: ({
    priceCalloutProperties,
    paywallParam,
    brandColor,
  }: PriceCalloutDefaultHelperProps) => ({
    callout_color:
      priceCalloutProperties?.callout_color ||
      brandColor?.light ||
      PRICE_CALLOUT_DEFAULT_COLOR,
    prices: findPriceCalloutOrInitialize(
      paywallParam?.stripe_prices_attributes || [],
      priceCalloutProperties?.prices || [],
    ),
  }),
};
